/**
 * How the admin sidebar groups its sections (F-FE-N187).
 *
 * The sidebar used to be one flat list — up to 21 equally loud buttons for an
 * admin. This folds the full-POS sections into a few named groups, Bhookly 2.0
 * style, WITHOUT hiding anything: role / edition / feature filtering happens
 * before this runs and is untouched, and every item that reaches it comes out
 * exactly once.
 *
 * Pure so the rules are tested on their own (adminNavGroups.test.ts):
 *  - a role that sees FLAT_THRESHOLD items or fewer gets a plain list in
 *    today's order — a counter with three items has nothing to open;
 *  - a group left with one visible child shows that child as a plain row;
 *  - a group with no visible children disappears;
 *  - an item the layout doesn't name is appended, never dropped.
 */

export type NavSignal = 'danger' | 'warn' | 'ok' | 'muted'

export type NavLayoutEntry = string | { group: string; label: string; childIds: string[] }

export type NavNode<T> =
  | { kind: 'item'; item: T }
  | { kind: 'group'; id: string; label: string; items: T[] }

/** At or below this many visible items the nav stays a flat list. */
export const FLAT_THRESHOLD = 6

/** Top-to-bottom order of the full-POS nav. Strings are standalone rows. */
export const POS_NAV_LAYOUT: NavLayoutEntry[] = [
  'dashboard',
  { group: 'sell', label: 'Sell', childIds: ['counter', 'server', 'online-orders'] },
  // Daily closing is a once-a-day ritual with its own status dot — it stands
  // on its own right under Sell so it is never folded out of sight.
  'day-close',
  // Riders, their deliveries and the cash they hold (F-DLV-N1) — its own row,
  // not a till surface, so it sits outside Sell.
  'dispatch',
  { group: 'online', label: 'Online', childIds: ['website', 'delivery-zones'] },
  { group: 'menu-floor', label: 'Menu & floor', childIds: ['menu', 'tables', 'kitchen', 'stations'] },
  { group: 'kitchen', label: 'Kitchen', childIds: ['production', 'kitchen-flow', 'catering'] },
  'inventory',
  { group: 'marketing-group', label: 'Marketing', childIds: ['customers', 'marketing'] },
  { group: 'reports-expenses', label: 'Reports & expenses', childIds: ['reports', 'expenses', 'receipts', 'void-log'] },
  // Staff logins and Staff & Payroll (F-HR-N1) sit together; with payroll off
  // the group has one child and shows as the plain Staff row it always was.
  { group: 'team', label: 'Team', childIds: ['staff', 'hr'] },
  'settings',
]

export function buildNavTree<T extends { id: string }>(
  visible: T[],
  layout: NavLayoutEntry[],
  flatThreshold = FLAT_THRESHOLD,
): NavNode<T>[] {
  if (visible.length <= flatThreshold) {
    return visible.map((item) => ({ kind: 'item', item }))
  }

  const byId = new Map(visible.map((i) => [i.id, i]))
  const placed = new Set<string>()
  const tree: NavNode<T>[] = []

  for (const entry of layout) {
    if (typeof entry === 'string') {
      const it = byId.get(entry)
      if (it && !placed.has(entry)) {
        tree.push({ kind: 'item', item: it })
        placed.add(entry)
      }
      continue
    }
    const children = entry.childIds
      .map((id) => byId.get(id))
      .filter((it): it is T => !!it && !placed.has(it.id))
    children.forEach((c) => placed.add(c.id))
    if (children.length === 1) tree.push({ kind: 'item', item: children[0] })
    else if (children.length > 1) tree.push({ kind: 'group', id: entry.group, label: entry.label, items: children })
  }

  // Anything the layout doesn't know about still shows — at the bottom.
  for (const it of visible) {
    if (!placed.has(it.id)) tree.push({ kind: 'item', item: it })
  }
  return tree
}

/** The group that holds the current page, so it can open itself. */
export function activeGroupId<T extends { href: string }>(
  tree: NavNode<T>[],
  isActive: (href: string) => boolean,
): string | null {
  for (const n of tree) {
    if (n.kind === 'group' && n.items.some((i) => isActive(i.href))) return n.id
  }
  return null
}

const SIGNAL_RANK: Record<NavSignal, number> = { danger: 4, warn: 3, ok: 2, muted: 1 }

/** The most urgent of a group's child dots, shown on the group while it is closed. */
export function strongestSignal(signals: (NavSignal | undefined)[]): NavSignal | undefined {
  let best: NavSignal | undefined
  for (const s of signals) {
    if (s && (!best || SIGNAL_RANK[s] > SIGNAL_RANK[best])) best = s
  }
  return best
}
